import { csrfToken } from "@circle-react/components/CircleAi/shared";
import { useCallback, useEffect, useState } from "react";

import { t } from "@/i18n-js/instance";
import { postVoiceTranscription } from "@/react/components/CircleAi/Home/copilotHelpers";

import { useComposerError } from "../../ComposerError/useComposerError";
import { FALLBACK_RECORDING_MIME_TYPE } from "./constants";

export type VoiceInputStatus = "idle" | "recording" | "transcribing";

interface UseVoiceInputProps {
  disabled: boolean;
  onActiveChange?: (isActive: boolean) => void;
  onTranscript: (text: string) => void;
}

const isRecordingSupported = () => {
  if (typeof navigator === "undefined" || typeof MediaRecorder === "undefined")
    return false;

  const mediaDevices = navigator.mediaDevices;
  return Boolean(
    mediaDevices && typeof mediaDevices.getUserMedia === "function",
  );
};

export const useVoiceInput = ({
  disabled,
  onActiveChange,
  onTranscript,
}: UseVoiceInputProps) => {
  const showError = useComposerError(s => s.showError);
  const [status, setStatus] = useState<VoiceInputStatus>("idle");

  const showRecordingError = useCallback(() => {
    showError({
      title: t("circle_ai_home.voice.errors.recording_failed.title"),
      description: t(
        "circle_ai_home.voice.errors.recording_failed.description",
      ),
    });
  }, [showError]);

  const showTranscriptionError = useCallback(() => {
    showError({
      title: t("circle_ai_home.voice.errors.transcription_failed.title"),
      description: t(
        "circle_ai_home.voice.errors.transcription_failed.description",
      ),
    });
  }, [showError]);

  useEffect(() => {
    onActiveChange?.(status !== "idle");
  }, [onActiveChange, status]);

  const handleBlob = useCallback(
    async (blob: Blob) => {
      const audio =
        blob.type === ""
          ? new Blob([blob], { type: FALLBACK_RECORDING_MIME_TYPE })
          : blob;
      if (!audio.size) {
        setStatus("idle");
        showRecordingError();
        return;
      }

      setStatus("transcribing");
      try {
        const result = await postVoiceTranscription(audio, csrfToken());
        if ("error" in result) {
          showTranscriptionError();
          return;
        }
        if (result.text.trim() === "") return;

        onTranscript(result.text);
      } catch {
        showTranscriptionError();
      } finally {
        setStatus("idle");
      }
    },
    [onTranscript, showRecordingError, showTranscriptionError],
  );

  const startRecording = useCallback(() => {
    if (disabled || status !== "idle") return;

    const mediaDevices = navigator.mediaDevices;
    if (!isRecordingSupported() || !mediaDevices) {
      showRecordingError();
      return;
    }

    setStatus("recording");
  }, [disabled, showRecordingError, status]);

  const cancelRecording = useCallback(() => {
    setStatus("idle");
  }, []);

  return {
    cancelRecording,
    handleBlob,
    showRecordingError,
    startRecording,
    status,
  };
};
